import { VariantProps, cva } from 'class-variance-authority';
import { DetailedHTMLProps, FC, ForwardedRef, InputHTMLAttributes, ReactNode, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import styles from './FloatingInput.module.scss';

const inputVariants = cva(
    [
        styles['floating-input'],
        'block',
        'w-full h-[58px]',
        'font-medium text-grayscale-900',
        'bg-transparent border border-grayscale-300',
        'rounded-lg px-[14px] py-[16px]',
        'placeholder:text-transparent',
        'focus:border-primary focus:outline focus:outline-2 focus:outline-primary/10',
    ],
    {
        variants: {
            invalid: {
                true: ['border-danger-500 outline outline-danger-50 focus:border-danger-500 focus:outline-danger-50'],
                false: [],
            },
            valid: {
                true: [
                    'border-success-500 outline outline-success-50 focus:border-success-500 focus:outline-success-50',
                ],
                false: [],
            },
            disabled: {
                true: ['bg-[#FAFAFA] dark:bg-[#20262D] opacity-70'],
                false: [],
            },
        },
        defaultVariants: {
            disabled: false,
            invalid: false,
            valid: false,
        },
    }
);

export interface FloatingInputProps
    extends Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'placeholder' | 'size'>,
        VariantProps<typeof inputVariants> {
    ref?: ForwardedRef<HTMLInputElement>;
    label: ReactNode;
    labelClassName?: string;
}

const FloatingInput = forwardRef<HTMLInputElement, FloatingInputProps>((props, ref) => {
    const { label, labelClassName, invalid, valid, disabled, className, ...rest } = props;

    return (
        <div className="relative w-[inherit]">
            <input
                {...rest}
                ref={ref}
                placeholder=" "
                className={twMerge(inputVariants({ invalid, valid, disabled, className }))}
            />
            <label
                className={twMerge(
                    styles['floating-label'],
                    'absolute left-[1px] top-[1px] px-[14px] py-[16px] text-grayscale-500 pointer-events-none',
                    invalid && 'text-danger-500',
                    labelClassName
                )}
            >
                {label}
            </label>
        </div>
    );
});

export default FloatingInput;
